import React, { useState, useEffect } from "react";
import { Tab, TabList, Button, Spinner, Text } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Add20Filled, Edit20Regular, Dismiss20Regular, Save20Regular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";

import ResourcesAccountComponent from "./ResourcesAccountComponent";
import GreetingComponent from "./GreetingComponent";
import HolidaysComponent from "./HolidaysComponent";
import RecordingComponent from "./RecordingComponent";
import SurveyComponent from "./Survey/SurveyComponent";
import RoutingComponent from "./RoutingComponent";
import WorkflowTriggersComponent from "./WorkflowTriggersComponent";

import { emptyCallFlowObject } from "../GenericComponent/GenericData/emptyCallFlowObject";
import { GetCallFlow, AddCallFlow, UpdateCallFlow, GetAllCallFlow } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';

import "../CSS/CallFlowsComponent.css";

export default function CallFlowsComponent({ st_CallFlows, setCallFlows, ACTIVE_TENANT,  WORKFLOWS, Token, SCOPES }) {
  const { t } = useTranslation();
  const { callFlowId } = useParams();
  const navigate = useNavigate();
  const ability = useAbility();
  const isCreateMode = callFlowId === "newCallFlow";
  const isEditMode = !isCreateMode;
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState("resourcesAccount");
  const [callFlowData, setCallFlowData] = useState(emptyCallFlowObject);
  const [loadFailed, setLoadFailed] = useState(false);
  const { showSuccess, showError } = useNotification();

  // Fonction pour recharger tous les CallFlows depuis le backend
  const refreshCallFlows = async (token) => {
    try {
      const scopeNames = Array.isArray(SCOPES) ? SCOPES : (SCOPES ? Object.keys(SCOPES) : ["default"]);
      const callFlowPromises = scopeNames.map((scope) =>
        GetAllCallFlow(ACTIVE_TENANT, userId, scope, token)
      );
      const data = await Promise.all(callFlowPromises);
      
      // Gérer le cas où GetAllCallFlow retourne soit un objet, soit un tableau
      let allFlows = [];
      data.forEach(scopeData => {
        if (Array.isArray(scopeData)) {
          allFlows = [...allFlows, ...scopeData];
        } else if (scopeData && typeof scopeData === 'object') {
          allFlows = [...allFlows, ...Object.values(scopeData)];
        }
      });
      
      // Dédupliquer par ID au cas où le même flow apparaît dans plusieurs scopes
      const uniqueFlows = Array.from(
        new Map(allFlows.map(flow => [flow.id, flow])).values()
      );
      
      setCallFlows(uniqueFlows);
    } catch (error) {
      console.error("Error refreshing call flows:", error);
    }
  };

  // Load existing call flow
  useEffect(() => {
    const loadCallFlow = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        if (isEditMode) {
          // ✅ Toujours charger depuis le serveur pour avoir les données à jour
          console.log("Fetching call flow from backend...", callFlowId);
          
          const token = Token || (await getAccessToken(instance, activeAccount));
          
          // Trouver le scope depuis le cache (juste pour le scope)
          const existingFlow = st_CallFlows?.find((f) => f.id === callFlowId);
          const flowScope = existingFlow?.scope ;
          
          const response = await GetCallFlow(ACTIVE_TENANT, userId, callFlowId, flowScope, token);
          console.log("Call flow fetched from backend:", response);
          if (response) {
            setCallFlowData({ ...emptyCallFlowObject, ...response });
          } else {
            setLoadFailed(true);
          }
        } else {
          setCallFlowData(emptyCallFlowObject);
        }
      } catch (error) {
        console.error("Error loading call flow:", error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    loadCallFlow();
  }, [isEditMode, callFlowId]);

  // 🔐 Calculer les permissions basées sur le scope du callflow
  useEffect(() => {
    // En mode création sans scope, garder les permissions par défaut de emptyCallFlowObject
    if (!callFlowData?.scope || !ability) {
      return;
    }

    // Recalculer les permissions basées sur le scope
    if (callFlowData?.scope && ability) {
      const flowScope = callFlowData.scope;
      const calculatedPermissions = {
        resourceAccount: {
          canCreate: ability?.can("create", `Flow.resourceAccount@${flowScope}`),
          canRead: ability?.can("read", `Flow.resourceAccount@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.resourceAccount@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.resourceAccount@${flowScope}`),
        },
        greeting: {
          canCreate: ability?.can("create", `Flow.greetingState@${flowScope}`),
          canRead: ability?.can("read", `Flow.greetingState@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.greetingState@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.greetingState@${flowScope}`),
          messages: {
            canCreate: ability?.can("create", `Flow.messages@${flowScope}`),
            canRead: ability?.can("read", `Flow.messages@${flowScope}`),
            canUpdate: ability?.can("update", `Flow.messages@${flowScope}`),
            canDelete: ability?.can("delete", `Flow.messages@${flowScope}`),
          }
        },
        holidays: {
          canCreate: ability?.can("create", `Flow.holiday@${flowScope}`),
          canRead: ability?.can("read", `Flow.holiday@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.holiday@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.holiday@${flowScope}`),
          openHour: {
            canCreate: ability?.can("create", `Flow.OpenHour@${flowScope}`),
            canRead: ability?.can("read", `Flow.OpenHour@${flowScope}`),
            canUpdate: ability?.can("update", `Flow.OpenHour@${flowScope}`),
            canDelete: ability?.can("delete", `Flow.OpenHour@${flowScope}`),
          },
          holidayActions: {
            canCreate: ability?.can("create", `Flow.holidayActions@${flowScope}`),
            canRead: ability?.can("read", `Flow.holidayActions@${flowScope}`),
            canUpdate: ability?.can("update", `Flow.holidayActions@${flowScope}`),
            canDelete: ability?.can("delete", `Flow.holidayActions@${flowScope}`),
          }
        },
        routing: {
          canCreate: ability?.can("create", `Flow.routing@${flowScope}`),
          canRead: ability?.can("read", `Flow.routing@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.routing@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.routing@${flowScope}`),
        },
        recording: {
          canCreate: ability?.can("create", `Flow.recording@${flowScope}`),
          canRead: ability?.can("read", `Flow.recording@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.recording@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.recording@${flowScope}`),
        },
        survey: {
          canCreate: ability?.can("create", `Flow.survey@${flowScope}`),
          canRead: ability?.can("read", `Flow.survey@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.survey@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.survey@${flowScope}`),
        },
        reports: {
          canCreate: ability?.can("create", `Flow.reports@${flowScope}`),
          canRead: ability?.can("read", `Flow.reports@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.reports@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.reports@${flowScope}`),
        },
        workflow: {
          canCreate: ability?.can("create", `Flow.workflow@${flowScope}`),
          canRead: ability?.can("read", `Flow.workflow@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.workflow@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.workflow@${flowScope}`),
        },
      };

      // Ajouter les permissions directement à l'objet callFlowData
      setCallFlowData(prev => ({
        ...prev,
        permissions: calculatedPermissions
      }));
    }
  }, [callFlowData?.scope, ability]);

  // Normalize call flow data to match old app format
  const normalizeCallFlowData = (data) => {
    const normalized = JSON.parse(JSON.stringify(data)); // Deep clone
    
    // Fix audio types: convert "text2speech" to "Audiofile" where needed
    // Also fix casing: "Text2speech" -> "text2speech"
    if (normalized.greeting) {
      Object.keys(normalized.greeting).forEach(key => {
        // Fix casing
        if (normalized.greeting[key]?.type === "Text2speech") {
          normalized.greeting[key].type = "text2speech";
        }
        // Convert to Audiofile if no text
        if (normalized.greeting[key]?.type === "text2speech" && !normalized.greeting[key]?.text) {
          normalized.greeting[key].type = "Audiofile";
        }
      });
    }
    
    // Fix holidays audio type - should be Audiofile if no text
    if (normalized.deskOpen?.holidays?.audio) {
      if (normalized.deskOpen.holidays.audio.type === "text2speech" && !normalized.deskOpen.holidays.audio.text) {
        normalized.deskOpen.holidays.audio.type = "Audiofile";
      }
    }
    
    // Fix holidays daysOff date format: convert ISO to DD/MM/YYYY
    if (normalized.deskOpen?.holidays?.daysOff) {
      normalized.deskOpen.holidays.daysOff = normalized.deskOpen.holidays.daysOff.map(day => {
        if (day.start && day.start.includes('-')) {
          const [year, month, d] = day.start.split('-');
          day.start = `${d}/${month}/${year}`;
        }
        if (day.end && day.end.includes('-')) {
          const [year, month, d] = day.end.split('-');
          day.end = `${d}/${month}/${year}`;
        }
        return day;
      });
    }
    
    // Fix open hours audio type - use simple "text2speech" not composite
    if (normalized.deskOpen?.openHours?.audio?.type) {
      if (normalized.deskOpen.openHours.audio.text) {
        normalized.deskOpen.openHours.audio.type = "text2speech";
      } else {
        normalized.deskOpen.openHours.audio.type = "Audiofile";
      }
    }
    
    // Fix routing audio types and expectedDigitNumber
    if (normalized.routing) {
      Object.keys(normalized.routing).forEach(key => {
        // Fix audio type - use simple "text2speech" not composite
        if (normalized.routing[key]?.audio?.type) {
          if (normalized.routing[key].audio.text) {
            normalized.routing[key].audio.type = "text2speech";
          } else {
            normalized.routing[key].audio.type = "Audiofile";
          }
        }
        // Convert expectedDigitNumber to string
        if (normalized.routing[key]?.expectedDigitNumber && typeof normalized.routing[key].expectedDigitNumber === 'number') {
          normalized.routing[key].expectedDigitNumber = String(normalized.routing[key].expectedDigitNumber);
        }
      });
    }
    
    // Fix survey questions - add yesDigit for YES_OR_NO questions
    if (normalized.survey?.questions) {
      Object.keys(normalized.survey.questions).forEach(key => {
        const question = normalized.survey.questions[key];
        if (question.type === "YES_OR_NO" && !question.yesDigit) {
          question.yesDigit = "1";
        }
      });
    }
    
    // Fix survey goodByeMessage type casing
    if (normalized.survey?.goodByeMessage?.type === "Text2speech") {
      normalized.survey.goodByeMessage.type = "text2speech";
    }
    
    // Remove text property from survey goodByeMessage if empty
    if (normalized.survey?.goodByeMessage?.text === "") {
      delete normalized.survey.goodByeMessage.text;
    }
    
    return normalized;
  };

  const handleSave = () => {
    setSaving(true);

    return getAccessToken(instance, activeAccount)
      .then((token) => {
        // Créer une copie sans permissions pour l'envoi au backend
        const { permissions, ...callFlowDataWithoutPermissions } = callFlowData;
        // Normalize data format
        const normalizedData = normalizeCallFlowData(callFlowDataWithoutPermissions);

        if (isCreateMode) {
          return AddCallFlow(ACTIVE_TENANT, userId, callFlowData.scope, token, normalizedData)
            .then(async (result) => {
              if (result?.success === true || result?.message === "Created") {
                // Ajouter le nouveau callflow au Redux immédiatement
                const newFlow = { ...normalizedData, scope: callFlowData.scope };
                const updatedFlows = [...(st_CallFlows || []), newFlow];
                setCallFlows(updatedFlows);
                
                showSuccess(t("CALLFLOW_CREATED_SUCCESS"));
                navigate("/callFlows");
              } else {
                const errorMsg = result?.message || "Erreur inconnue";
                console.error("Échec création CallFlow:", result);
                showError(t("CALLFLOW_CREATION_FAILED"), errorMsg);
              }
            });
        } else {
          return UpdateCallFlow(ACTIVE_TENANT, userId, callFlowData.id, callFlowData.scope, token, normalizedData)
            .then(async (result) => {
              if (result?.success === true || result?.message === "Updated") {
                // Mettre à jour le Redux immédiatement avec les nouvelles données
                const updatedFlows = st_CallFlows?.map(flow => 
                  flow.id === callFlowData.id ? { ...normalizedData, scope: callFlowData.scope } : flow
                ) || [{ ...normalizedData, scope: callFlowData.scope }];
                
                setCallFlows(updatedFlows);
                
                showSuccess(t("CALLFLOW_UPDATED_SUCCESS"));
                navigate("/callFlows");
              } else {
                const errorMsg = result?.message || "Erreur inconnue";
                console.error("Échec mise à jour CallFlow:", result);
                showError(t("CALLFLOW_UPDATE_FAILED"), errorMsg);
              }
            });
        }
      })
      .catch((err) => {
        console.error("Error saving call flow:", err);
        showError(isCreateMode ? t("CALLFLOW_CREATION_ERROR") : t("CALLFLOW_UPDATE_ERROR"), err?.message);
      })
      .finally(() => setSaving(false));
  };
  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="tab-loading">
          <Spinner label={t("LOADING")} size="large" />
        </div>
      );
    }

    if (loadFailed) {
      return (
        <div className="tab-error">
          <Text weight="semibold">{t("ERROR_LOADING_CALL_FLOW")}</Text>
        </div>
      );
    }

    switch (selectedTab) {

      case "resourcesAccount":
        return <ResourcesAccountComponent callFlowData={callFlowData} setCallFlowData={setCallFlowData} isEditMode={isEditMode} permissions={callFlowData.permissions} />;
      case "greeting":
        return <GreetingComponent callFlowData={callFlowData} setCallFlowData={setCallFlowData} isEditMode={isEditMode} ACTIVE_TENANT={ACTIVE_TENANT} permissions={callFlowData.permissions} />;
      case "holidays":
        return <HolidaysComponent callFlowData={callFlowData} setCallFlowData={setCallFlowData} isEditMode={isEditMode} ACTIVE_TENANT={ACTIVE_TENANT} permissions={callFlowData.permissions} />;
      case "routing":
        return <RoutingComponent callFlowData={callFlowData} setCallFlowData={setCallFlowData} isEditMode={isEditMode} ACTIVE_TENANT={ACTIVE_TENANT} permissions={callFlowData.permissions} />;
      case "recording":
        return <RecordingComponent callFlowData={callFlowData} setCallFlowData={setCallFlowData} isEditMode={isEditMode} ACTIVE_TENANT={ACTIVE_TENANT} permissions={callFlowData.permissions} />;
      case "survey":
        return <SurveyComponent callFlowData={callFlowData} setCallFlowData={setCallFlowData} isEditMode={isEditMode} ACTIVE_TENANT={ACTIVE_TENANT} permissions={callFlowData.permissions} />;
      case "workflows":
        return <WorkflowTriggersComponent  workflows={WORKFLOWS} callFlowData={callFlowData} setCallFlowData={setCallFlowData} isEditMode={isEditMode} ACTIVE_TENANT={ACTIVE_TENANT} permissions={callFlowData.permissions} />;
      default:
        return null;
    }
  };

  return (
    <div className="callflows-container">
      {!loading && loadFailed && (
        <div className="top-error">
          <Text weight="semibold">{t("CALL_FLOW_NOT_FOUND")}</Text>
        </div>
      )}

      <div className="header-wrapper">
        <TabList selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value)} className="tab-list">
          {/* 🔐 Afficher le tab si l'utilisateur peut LIRE + (CRÉER en mode création OU MODIFIER en mode édition) */}
          {callFlowData.permissions?.resourceAccount?.canRead && 
           ( <Tab value="resourcesAccount">{t("RESOURCES_ACCOUNT")}</Tab>
          )}
          
          {isEditMode && (
            <>
              {callFlowData.permissions?.greeting?.canRead &&  (
                <Tab value="greeting">{t("GREETING_MESSAGE")}</Tab>
              )}
              
              {callFlowData.permissions?.holidays?.canRead &&  (
                <Tab value="holidays">{t("HOLIDAYS")}</Tab>
              )}
              
              {callFlowData.permissions?.routing?.canRead &&  (
                <Tab value="routing">{t("ROUTING")}</Tab>
              )}
              
              {callFlowData.permissions?.recording?.canRead &&  (
                <Tab value="recording">{t("RECORDING")}</Tab>
              )}
              
              {callFlowData.permissions?.survey?.canRead &&  (
                <Tab value="survey">{t("SURVEY")}</Tab>
              )}
              
              {callFlowData.permissions?.workflow?.canRead &&  (
                <Tab value="workflows">{t("WORKFLOWS")}</Tab>
              )}
            </>
          )}
        </TabList>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 10px 0" }}>
          <div style={{ flex: 1 }}></div>
          {callFlowData?.displayName && <p className="callflow-display-name" style={{ flex: 1, textAlign: "center", margin: 0 }}>{callFlowData.displayName}</p>}
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button
              icon={<Dismiss20Regular />}
              appearance="secondary"
              onClick={() => navigate("/callFlows")}
            >
              {t("CANCEL")}
            </Button>
            <Button
              icon={saving ? <Spinner size="tiny" /> : (isEditMode ? <Edit20Regular /> : <Save20Regular />)}
              appearance="primary"
              className="btn-save-style"
              onClick={handleSave}
              disabled={saving || (isCreateMode && (!callFlowData?.id || !callFlowData?.displayName))}
            >
              {t("SAVE")}
            </Button>
          </div>
        </div>
      </div>

      <div className="callflow-tab-content" >{renderTabContent()}</div>
    </div>
  );
}
